import { Injectable } from '@angular/core';
import { IAcademicYear } from 'app/interfaces/academic-year.interface';
import { ApiRoutes } from 'app/routes/app.routes';
import { HttpService } from '../http/http.service';


@Injectable({
  providedIn: 'root'
})
export class AcademicYearsService {

  private routes = ApiRoutes.api;
  constructor(private httpService: HttpService) { }

  addAcademicYear(payload: any) {
    return this.httpService.makeRequest({
      path: this.routes.academicYear.add,
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  allAcademicYears(pagination?: { page?: number; limit?: number }) {
    const params: any = {};

    if (pagination) {
      if (pagination.page) params.page = pagination.page;
      if (pagination.limit) params.limit = pagination.limit;
    }

    return this.httpService.makeRequest({
      path: this.routes.academicYear.retrieve,
      method: 'get',
      options: { params },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  getAcademicYear(id: number) {
    return this.httpService.makeRequest({
      path: this.routes.academicYear.retrieveSingle,
      method: 'get',
      options: { id },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  editAcademicYear(id: string, payload: any) {
    return this.httpService.makeRequest({
      path: this.routes.academicYear.edit,
      method: 'put',
      options: { id, body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  deleteAcademicYear(id: string) {
    return this.httpService.makeRequest({
      path: this.routes.academicYear.delete,
      method: 'delete',
      options: { id },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  setActiveAcademicYear(is_active: boolean, id: string) {
    return this.httpService.makeRequest({
      path: this.routes.academicYear.setActiveYear,
      method: 'post',
      options: { id, body: { is_active } },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  bulkDelete(payload: { ids: number[] }) {
    return this.httpService.makeRequest({
      path: this.routes.academicYear.bulkDelete,
      method: 'delete',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Bulk delete academic years
   * @param payload - Object containing array of academic year IDs to delete
   * @returns Observable with the delete response
   */
  bulkDeleteAcademicYear(payload: { ids: number[] }) {
    return this.bulkDelete(payload);
  }

  /**
   * Promotes all learners at the end of an academic year
   * @param academicYearId - The ID of the academic year
   */
  promoteLearnersAtYearEnd(academicYearId: number) {
    return this.httpService.makeRequest({
      path: this.routes.academicYear.promoteLearnersAtYearEnd,
      method: 'post',
      options: {
        body: {
          payload: {
            academic_year_id: academicYearId
          }
        }
      },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Duplicates an academic year with all its related data
   * @param sourceYearId - The ID of the source academic year to duplicate
   * @param newYearName - Name for the new academic year
   * @param includeLearners - Whether to include learners in the duplication (optional, defaults to false)
   */
  duplicateAcademicYear(
    sourceYearId: number,
    newYearName: string,
    includeLearners: boolean = false
  ) {
    // According to backend: sourceYearId can be in URL path or payload
    // We'll use URL path (via id) and also include it in payload for consistency
    return this.httpService.makeRequest({
      path: this.routes.academicYear.duplicate || 'duplicate-academic-year',
      method: 'post',
      options: {
        id: sourceYearId.toString(),
        params: {
          include_learners: includeLearners.toString()
        },
        body: {
          source_year_id: sourceYearId,
          new_year_name: newYearName,
        }
      },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }
}
